import EditRoomPlugin from "@edit-room/EditRoomPlugin.tsx"
import { PosScreen, router } from '@/pos/PosRouter.ts';
import { batch, computed, deepSignal, effectOn, mutable, onMount, selector, signal } from '@/react/core/reactive.ts';
import { Room, RoomObject, type RoomObjectType, type TreeInfo } from '@/data/Room.ts';
import _ from 'lodash';
import { dataLock } from '@/data/DataUtils.ts';
import { convertDocument, convertDocuments } from '@/data/data-utils.ts';
import { clone } from '@atc-group/json-fn';
import uuid from 'time-uuid';
import { memo } from "react";
import { toast } from "react-toastify";
import { mainScreen } from "@/data/PosSettingsSignal.ts";
import { userFLow } from "@/shared/logger.ts";
import { loginUser } from "@/data/UserSignal.ts";
import { partitions0 } from "@/data/PartitionHub.ts";

export const TABLE_COLORS = ["#FFFFFF", "#CEEFAB", "#B1F46A", "#FFF59D", "#FFD363", "#A4866A", "#C8AAFF", "#FFDBE7", "#A5D9FF", "#6AB2F4", "#73F8F8"];
export const WALL_COLORS = ["#FFFFFF", "#7E858E", "#5C68B1", "#BA5EBA", "#CE3100", "#4C8D1F", "#7D5B2F", "#CEEFAB", "#007C7C"];

export enum EditorMode {
  Room = "room",
  Table = "table",
  Wall = "wall",
  Tree = "tree"
}

export const LIST_TREE: TreeInfo[] = [
  { id: 1, name: "Tree 1", treeSrc: "/tree-1@2x.png" },
  { id: 2, name: "Tree 2", treeSrc: "/tree-2@2x.png" },
  { id: 3, name: "Tree 3", treeSrc: "/tree-3@2x.png" },
  { id: 4, name: "Tree 4", treeSrc: "/tree-4@2x.png" },
  { id: 5, name: "Tree 5", treeSrc: "/tree-5@2x.png" },
  { id: 6, name: "Tree 6", treeSrc: "/tree-6@2x.png" },
  { id: 7, name: "Tree 7", treeSrc: "/tree-7@2x.png" },
  { id: 8, name: "Tree 8", treeSrc: "/tree-8@2x.png" },
  { id: 9, name: "Tree 9", treeSrc: "/tree-9@2x.png" },
  { id: 10, name: "Tree 10", treeSrc: "/tree-10@2x.png" },
  { id: 11, name: "Tree 11", treeSrc: "/tree-11@2x.png" },
  { id: 12, name: "Tree 12", treeSrc: "/tree-12@2x.png" },
  { id: 13, name: "Tree 13", treeSrc: "/tree-13@2x.png" },
  { id: 14, name: "Tree 14", treeSrc: "/tree-14@2x.png" },
  { id: 15, name: "Tree 15", treeSrc: "/tree-15@2x.png" },
  { id: 16, name: "Tree 16", treeSrc: "/tree-16@2x.png" },
  { id: 17, name: "Tree 17", treeSrc: "/tree-17@2x.png" },
  { id: 18, name: "Tree 18", treeSrc: "/tree-18@2x.png" },
  { id: 19, name: "Tree 19", treeSrc: "/tree-19@2x.png" },
  { id: 20, name: "Tree 20", treeSrc: "/tree-20@2x.png" },
  { id: 21, name: "Tree 21", treeSrc: "/tree-21@2x.png" },
  { id: 22, name: "Tree 22", treeSrc: "/tree-22@2x.png" },
  { id: 23, name: "Tree 23", treeSrc: "/tree-23@2x.png" },
  { id: 24, name: "Tree 24", treeSrc: "/tree-24@2x.png" },
  { id: 25, name: "Tree 25", treeSrc: "/tree-25@2x.png" },
  { id: 26, name: "Tree 26", treeSrc: "/tree-26@2x.png" },
  { id: 27, name: "Tree 27", treeSrc: "/tree-27@2x.png" },
  { id: 28, name: "Tree 28", treeSrc: "/tree-28@2x.png" },
  { id: 29, name: "Tree 29", treeSrc: "/tree-29@2x.png" },
  { id: 30, name: "Tree 30", treeSrc: "/tree-30@2x.png" },
  { id: 31, name: "Tree 31", treeSrc: "/tree-31@2x.png" },
  { id: 32, name: "Tree 32", treeSrc: "/tree-32@2x.png" },
];
export const [mode0, setMode0] = signal<EditorMode>(EditorMode.Room);
export const isSelectedMode = selector(mode0)
export const [roomObject0, setRoomObject0] = signal<RoomObject | undefined>();
export const [rooms0, setRooms0] = signal<Array<Room>>([]);

//@ts-ignore
window.rooms1 = rooms0

export const [openEditRomDrawer, setOpenEditRoomDrawer] = signal(false)
export const [openEditPartitionDrawer, setOpenEditPartitionDrawer] = signal(false)

const roomObjects0 = computed(() => rooms0().reduce((acc: RoomObject[], curr) => [...acc, ...(curr.roomObjects || [])], []))

export const [activeRoomId, setActiveRoomId] = signal<string>(""),
  isActiveRoomId = selector<string>(activeRoomId)
export const activeRoom = computed(() => rooms0().find(r => r._id === activeRoomId()));
export const activeRoomIndex = computed(() => rooms0().findIndex(r => r._id === activeRoomId()));

// set mode on activeObject change
effectOn([roomObject0], () => {
  switch (roomObject0()?.type ) {
    case 'table':
      setMode0(EditorMode.Table);
      break;
    case 'wall':
      setMode0(EditorMode.Wall);
      break;
    case 'tree':
      setMode0(EditorMode.Tree)
  }
})

//Edit Room Form handlers

export async function deleteRoom() {
  const _activeRoom = activeRoom();
  setRooms0((rooms) => {
    rooms.splice(activeRoomIndex(), 1);
    return [...rooms];
  });
  await _activeRoom?.doc?.incrementalRemove();
  //then set current room
  setActiveRoomId(rooms0()?.[0]?._id)
}

export async function createRoom() {
  const room = {
    _id: uuid(),
    name: "New Room",
    order: Math.max(...rooms0().map(r => r.order)) + 1,
    roomObjects: []
  };
  const roomDoc = await Room.insert(clone(room))
  const newRoom = convertDocument<Room>(roomDoc, true, ['roomObjects'])
  setRooms0(rooms => [...rooms, newRoom]);
}

export async function liftRoom(direction: 'UP' | 'DOWN') {
  //swap order for current room and adjacent room
  const _activeRoom = activeRoom()
  const adjacentRoomIndex = activeRoomIndex() + (direction === 'UP' ? -1 : 1);
  if (adjacentRoomIndex < 0 || adjacentRoomIndex >= rooms0().length || !_activeRoom) return;
  const adjacentRoom = rooms0()[adjacentRoomIndex];
  const tempAdjacentRoomOrder = adjacentRoom.order
  adjacentRoom.order = _activeRoom.order;
  _.assign(activeRoom(), {order: tempAdjacentRoomOrder})
  setRooms0(prev => [...prev
    .sort((r1, r2) => r1.order - r2.order)])
}

//Edit Table Form handlers
export const copyRoomObject = async () => {
  //getActiveRoom
  if (!roomObject0()) return;
  const activeRoom = rooms0().find(r => r._id === activeRoomId());
  const _roomObject= mutable<RoomObject>({
    ...clone(roomObject0()!), _id: uuid()
  });
  if (_roomObject.type === "table") _roomObject.name = newObjectName();
  _roomObject.location.y += _roomObject.size.height + 10;
  const roomObjectDoc = await RoomObject.insert(clone(_roomObject))
  const newRoomObject = convertDocument(roomObjectDoc, true);
  activeRoom?.roomObjects?.push(newRoomObject);
  setRoomObject0(newRoomObject);
}

export const roomObjects1 = computed<RoomObject[]>(() => {
  const roomObjectsFromPartitions = partitions0()?.flatMap(partition => partition.roomObjects || []);
  const roomObjectsFromRooms = rooms0()?.flatMap(room => room.roomObjects || []);
  const combinedRoomObjects = [...roomObjectsFromPartitions, ...roomObjectsFromRooms];
  const uniqueObjectsMap = new Map();
  combinedRoomObjects.forEach(roomObject => {
    if (roomObject.name && !uniqueObjectsMap.has(roomObject.name)) {
      uniqueObjectsMap.set(roomObject.name, roomObject);
    }
  });
  return Array.from(uniqueObjectsMap.values());
}) //fixme: for rooms0 && partitions0

export const isTableNameExist = (tableName: string) => {
  return roomObjects0()?.findIndex(value => value.name === tableName) !== -1
  // roomObjects1()?.findIndex(value => value.name ===  tableName) !== -1
}
export const newObjectName = () => {
  let res = 1
  while (isTableNameExist(res.toString())) res++
  return res.toString();
}

export const objectSizeNameConstructor = (type: RoomObjectType, treeInfo?: TreeInfo) => {
  if (type === 'table')
    return {
      size: {
        width: 76,
        height: 75
      },
      borderRadius: {
        topLeft: 4,
        bottomLeft: 4,
        bottomRight: 4,
        topRight: 4
      },
      name: newObjectName(),
    }
  else if (type === 'tree'){
    return {
      size: {
        width: 76,
        height: 75
      },
      treeInfo: treeInfo
    }
  }
  return {
    size: {
      width: 200,
      height: 20
    }}
}

export const createRoomObject = async (type: RoomObjectType, treeInfo?: TreeInfo) => {
  userFLow(`create room ${type}`, {
    username: loginUser()?.name
  });
  //getActiveRoom
  const activeRoom = rooms0().find(r => r._id === activeRoomId());
  if (!activeRoom){
    return toast.error('Please create a room first!')
  }
  const _roomObject: RoomObject = {
     _id: uuid(),
    type,
    room: activeRoom?._id,
    location: {
      x: 10,
      y: 10
    },
    ...objectSizeNameConstructor(type,treeInfo),
    shape: 'rectangle',
    bgColor: '#FFFFFF'
  };
  const roomObjectDoc = await RoomObject.insert(clone(_roomObject))
  const newRoomObject = convertDocument<RoomObject>(roomObjectDoc, true)
  activeRoom?.roomObjects?.push(newRoomObject);
  setRoomObject0(newRoomObject);
}

//</editor-fold>
export const deleteRoomObject = async () => {
  const activeRoom = rooms0().find(r => r._id === activeRoomId());
  let _roomObject = roomObject0();
  const index = activeRoom?.roomObjects?.findIndex(o => o._id === _roomObject?._id);
  if (index !== -1 && index !== undefined) activeRoom?.roomObjects?.splice(index!, 1);
  await _roomObject?.doc?.incrementalRemove();
  setRoomObject0();
};

export const updateObjectName = (name: string) => {
  if (isTableNameExist(name)) return false;
  _.assign(roomObject0(), { name });
  return true;
}

export const onBack = () => {
  userFLow(`click back`, {
    username: loginUser()?.name
  });
  router.screen = mainScreen();
};

export const handleOnMount = async () => {
  await dataLock.acquireAsync();
  const roomsRaw = await Room.find().exec();
  const rooms = convertDocuments<Room>(roomsRaw, true, ['roomObjects']);
  const roomObjectsRaw = await RoomObject.find().exec();
  const roomObjects = convertDocuments<RoomObject>(roomObjectsRaw, true);
  setRoomObject0(roomObjects[0])
  console.log('room objects', roomObjects.length);

  rooms
    .sort((r1, r2) => r1.order - r2.order)
    .forEach(r => {
      r.roomObjects = r.roomObjects || [];
      r.roomObjects?.push(...roomObjects.filter(o => o.room === r._id).filter((o: {
        type: string;
      }) => o.type === "table" || o.type === "wall" || o.type === 'tree'));
    });
  batch(() => {
    setRooms0(rooms);
    setActiveRoomId(rooms[0]?._id);
  });
  console.log('room objects 2');
  RoomObject.$.subscribe((e) => {
    console.log('change: ', e);
  })
  //todo: reactive
}

const EditRoomView = () => {

  onMount(async () => {
    await handleOnMount()
  });
  return <EditRoomPlugin />
}

export default memo(EditRoomView);